import { LASER_BASE_PRESETS } from '../config/defaults';

export const APP_TEMPLATE = `
  <div class="app-shell projection-only">
    <aside class="control-panel" aria-label="Parámetros de simulación">
      <header class="brand">
        <div class="brand-mark" aria-hidden="true"><span></span><span></span><span></span></div>
        <div>
          <p class="eyebrow">VOLUMETRIC OPTICS LAB</p>
          <h1>HOLO—NET <sup>α</sup></h1>
          <span id="app-version" class="app-version">v—</span>
        </div>
      </header>

      <div class="phase-card">
        <span class="status-dot"></span>
        <div><b>Physics core online</b><small>Fases 1 + 2 + 3 · CPU / spatial hash</small></div>
        <span class="phase-number">03</span>
      </div>

      <div class="preset-row" aria-label="Presets de laboratorio">
        <label for="preset">Scale preset</label>
        <select id="preset">
          <option>INTERACTIVE</option><option>P0</option><option>DEBUG</option><option>SMALL</option>
          <option>MEDIUM</option><option>EXPERIMENTAL</option>
        </select>
      </div>

      <div class="parameter-scroll">
        <details open>
          <summary><span>01</span> Volume</summary>
          <div class="parameter-grid">
            <label>Cube edge <output id="cube-size-output">30 cm</output>
              <input id="cube-size" type="range" min="10" max="100" step="1" value="30" />
            </label>
          </div>
        </details>

        <details open>
          <summary><span>02</span> Micro-mesh field</summary>
          <div class="parameter-grid">
            <label>Distribution
              <select id="mesh-strategy">
                <option value="parallel">Parallel baseline</option>
                <option value="pseudo-random">Pseudo-random</option>
                <option value="optimized">Greedy optimized</option>
              </select>
            </label>
            <label>Filament pattern
              <select id="mesh-pattern">
                <option value="radial">Radial web</option>
                <option value="cartesian">Cartesian grid · cubic stack</option>
                <option value="triangular">Triangular</option>
                <option value="voronoi">Voronoi cells</option>
                <option value="random">Random network</option>
              </select>
            </label>
            <div class="two-columns">
              <label>Mesh count<input id="mesh-count" type="number" min="1" max="10000" step="1" value="24" /></label>
              <label>Density<input id="mesh-density" type="number" min="3" max="32" step="1" value="7" /></label>
            </div>
            <label>Minimum separation
              <select id="mesh-separation">
                <option value="0.0001">0.1 mm</option>
                <option value="0.0005" selected>0.5 mm</option>
                <option value="0.001">1.0 mm</option>
                <option value="0.002">2.0 mm</option>
              </select>
            </label>
            <label>Filament radius
              <select id="filament-radius">
                <option value="0.00005">50 μm</option>
                <option value="0.00012" selected>120 μm</option>
                <option value="0.00025">250 μm</option>
              </select>
            </label>
            <label>Orientation variance <output id="orientation-output">6.9°</output>
              <input id="orientation" type="range" min="0" max="25" step="0.5" value="6.9" />
            </label>
            <label>Curvature <output id="curvature-output">6.0 mm</output>
              <input id="curvature" type="range" min="0" max="15" step="0.5" value="6" />
            </label>
          </div>
        </details>

        <details open>
          <summary><span>03</span> Laser system</summary>
          <div class="parameter-grid">
            <label>Fixed laser / voxel pairs
              <select id="laser-count">
                <option value="16">16 · P0</option>
                <option value="100">100</option><option value="500">500</option>
                <option value="1000">1,000</option><option value="2000" selected>2,000</option>
                <option value="5000">5,000</option>
                <option value="10000">10,000</option>
              </select>
            </label>
            <label>Floor dome radius <output id="hemisphere-output">21 cm</output>
              <input id="hemisphere-radius" type="range" min="18" max="140" step="1" value="21" />
            </label>
            <label>Maximum polar angle <output id="angle-output">90°</output>
              <input id="laser-angle" type="range" min="15" max="90" step="1" value="90" />
            </label>
            <div class="spectral-row"><span class="wavelength-dot"></span><b>532 nm</b><small>monochromatic phase</small></div>
          </div>
        </details>

        <details>
          <summary><span>05</span> Physics & visibility</summary>
          <div class="parameter-grid toggles">
            <label><span>Physical first-hit calibration</span><input id="occlusion" type="checkbox" checked disabled /></label>
            <label><span>Micro-meshes</span><input id="show-meshes" type="checkbox" checked /></label>
            <label><span>Laser system & paths</span><input id="show-lasers" type="checkbox" checked /></label>
            <label><span>Desired paths</span><input id="show-desired" type="checkbox" /></label>
            <label><span>Impact points</span><input id="show-intersections" type="checkbox" checked /></label>
            <label><span>Blocked paths</span><input id="show-blocked" type="checkbox" checked /></label>
          </div>
        </details>
      </div>

      <div class="control-actions">
        <button id="run-simulation" class="primary-button"><span>RUN SIMULATION</span><kbd>↵</kbd></button>
        <div class="secondary-actions">
          <button id="export-config">Export config</button>
          <button id="import-config">Import</button>
          <button id="export-csv">Ray CSV</button>
          <input id="config-file" type="file" accept="application/json,.json" hidden />
        </div>
      </div>
    </aside>

    <main class="workspace">
      <button id="toggle-lab" class="lab-toggle" type="button" aria-pressed="false">Abrir laboratorio</button>
      <header class="topbar">
        <nav class="mode-tabs" aria-label="Modo de visualización">
          <button data-mode="STRUCTURE">Structure<small>01</small></button>
          <button data-mode="LASERS">Lasers<small>02</small></button>
          <button data-mode="OCCLUSION">Occlusion<small>04</small></button>
          <button class="active" data-mode="TARGET">Projection<small>05</small></button>
          <button data-mode="VOXELS">Voxels<small>03</small></button>
          <button data-mode="HEATMAP" data-future>Heatmap<small>06</small></button>
          <button data-mode="FINAL_IMAGE" data-future>Final image<small>08</small></button>
        </nav>
        <div class="topbar-actions">
          <button id="reset-camera" aria-label="Restablecer cámara">Reset view</button>
          <span id="webgl-badge">WEBGL</span>
        </div>
      </header>

      <section id="viewport" class="viewport">
        <div class="viewport-title">
          <span id="mode-index">HOLO—NET</span>
          <h2 id="mode-title">PROYECCIÓN LÁSER 3D</h2>
          <p id="mode-description">Puntos de luz reales sobre filamentos físicos</p>
          <small id="hardware-model" class="hardware-model">500 láseres fijos · 500 vóxeles · relación 1:1</small>
        </div>
        <div id="projection-toolbar" class="projection-toolbar">
          <label class="projection-select" for="projection-figure">
            <span>Figura</span>
            <select id="projection-figure">
              <option value="HEART" selected>FORMA · CORAZÓN</option>
              <option value="SPHERE">FORMA · ESFERA</option>
              <option value="CUBE">FORMA · CUBO</option>
              <option value="HELIX">FORMA · ESPIRAL</option>
              <option value="STAR">FORMA · ESTRELLA</option>
              <option value="TORUS">FORMA · TOROIDE</option>
              <option value="DNA">FORMA · ADN</option>
              <option value="PYRAMID">FORMA · PIRÁMIDE</option>
            </select>
          </label>
          <label class="projection-select" for="projection-precision">
            <span>Precisión física</span>
            <select id="projection-precision">
              <option value="16">P0 · 16 VÓXELES</option>
              <option value="100">PRECISIÓN · 100 VÓXELES</option>
              <option value="500">PRECISIÓN · 500 VÓXELES</option>
              <option value="1000">PRECISIÓN · 1.000 VÓXELES</option>
              <option value="2000" selected>PRECISIÓN · 2.000 VÓXELES</option>
              <option value="5000">PRECISIÓN · 5.000 VÓXELES</option>
              <option value="10000">PRECISIÓN · 10.000 VÓXELES</option>
            </select>
          </label>
          <label class="projection-select" for="projection-mesh-pattern">
            <span>Tipo de malla</span>
            <select id="projection-mesh-pattern">
              <option value="radial" selected>MALLA · RADIAL</option>
              <option value="cartesian">MALLA · CÚBICA</option>
            </select>
          </label>
          <label class="projection-select" for="projection-laser-base">
            <span>Proporción del prototipo</span>
            <select id="projection-laser-base">
              <option value="COMPACT" selected>${LASER_BASE_PRESETS.COMPACT.label}</option>
              <option value="ORIGINAL">${LASER_BASE_PRESETS.ORIGINAL.label}</option>
              <option value="CUSTOM" hidden>BASE · PERSONALIZADA</option>
            </select>
          </label>
          <label id="projection-mesh-control" class="projection-rays projection-mesh" for="show-projection-mesh">
            <span class="projection-rays-copy">
              <b>Malla física</b>
              <small>Muestra los filamentos que sostienen los vóxeles</small>
            </span>
            <span class="projection-rays-action">
              <strong id="projection-mesh-state">MOSTRAR</strong>
              <input id="show-projection-mesh" type="checkbox" aria-label="Mostrar la malla física en la proyección" />
            </span>
          </label>
          <label id="projection-laser-control" class="projection-rays" for="show-projection-rays">
            <span class="projection-rays-copy">
              <b>Rayos láser</b>
              <small>Une cada punto con su emisor</small>
            </span>
            <span class="projection-rays-action">
              <strong id="projection-laser-state">OFF · SHOW</strong>
              <input id="show-projection-rays" type="checkbox" aria-label="Show laser rays in projection" />
            </span>
          </label>
          <label id="projection-motion-control" class="projection-motion active" for="animate-projection">
            <span class="projection-motion-copy">
              <b>Movimiento 3D</b>
              <small>Direcciones X/Y/Z y origen configurable</small>
            </span>
            <span class="projection-motion-action">
              <strong id="projection-motion-state">RUNNING</strong>
              <input id="animate-projection" type="checkbox" aria-label="Animate projection by switching lasers" checked />
            </span>
          </label>
          <details class="projection-popover rotation-popover">
            <summary><b>CONTROL 3D</b><small>Giro y origen</small></summary>
            <div class="projection-popover-content">
              <header><b>Dirección de giro</b><small>Selecciona cada eje por separado</small></header>
              <div class="rotation-axis-grid">
                <label><span>X</span><select id="rotation-x-direction" aria-label="Dirección de giro X">
                  <option value="-1">−</option><option value="0" selected>OFF</option><option value="1">+</option>
                </select></label>
                <label><span>Y</span><select id="rotation-y-direction" aria-label="Dirección de giro Y">
                  <option value="-1">−</option><option value="0">OFF</option><option value="1" selected>+</option>
                </select></label>
                <label><span>Z</span><select id="rotation-z-direction" aria-label="Dirección de giro Z">
                  <option value="-1">−</option><option value="0" selected>OFF</option><option value="1">+</option>
                </select></label>
              </div>
              <div class="rotation-sliders">
                <label>Origen X <output id="pivot-x-output">0%</output>
                  <input id="pivot-x" type="range" min="-100" max="100" step="5" value="0" />
                </label>
                <label>Origen Y <output id="pivot-y-output">0%</output>
                  <input id="pivot-y" type="range" min="-100" max="100" step="5" value="0" />
                </label>
                <label>Origen Z <output id="pivot-z-output">0%</output>
                  <input id="pivot-z" type="range" min="-100" max="100" step="5" value="0" />
                </label>
                <label>Duración de vuelta <output id="rotation-period-output">8.0 s</output>
                  <input id="rotation-period" type="range" min="2" max="24" step="0.5" value="8" />
                </label>
              </div>
              <button id="reset-rotation" class="popover-button" type="button">CENTRAR CONTROL</button>
            </div>
          </details>
          <details class="projection-popover voxel-test-popover">
            <summary><b>TEST DE VÓXELES</b><small id="voxel-coverage-summary">Calculando cobertura…</small></summary>
            <div class="projection-popover-content">
              <header><b>Comprobación secuencial</b><small>Un láser y un vóxel en cada paso</small></header>
              <div id="voxel-coverage-result" class="voxel-coverage-result">PENDIENTE</div>
              <small>Comprueba el catálogo de impactos. La reproducción de la figura se mide en CALIDAD.</small>
              <div class="rotation-sliders">
                <label>Velocidad <output id="voxel-test-speed-output">4.0 vóxeles/s</output>
                  <input id="voxel-test-speed" type="range" min="0.5" max="20" step="0.5" value="4" />
                </label>
                <label>Iluminación <output id="voxel-test-duration-output">160 ms</output>
                  <input id="voxel-test-duration" type="range" min="20" max="1000" step="20" value="160" />
                </label>
              </div>
              <div class="voxel-test-progress"><i id="voxel-test-progress-bar"></i></div>
              <small id="voxel-test-current">LISTO PARA INICIAR</small>
              <button id="voxel-test-toggle" class="popover-button" type="button">INICIAR TEST</button>
            </div>
          </details>
          <details class="projection-popover quality-popover">
            <summary><b>CALIDAD / P0</b><small id="quality-summary">Calculando figura…</small></summary>
            <div class="projection-popover-content">
              <header><b>Reproducción de la figura</b><small>Primer impacto físico · tolerancia explícita</small></header>
              <p>Figura reproducida <b id="quality-coverage">—</b></p>
              <p>Activados / solicitados <b id="quality-count">—</b></p>
              <p>Sin asignación <b id="quality-unmatched">—</b></p>
              <p>Tolerancia aplicada <b id="quality-tolerance">—</b></p>
              <p>Error RMS <b id="quality-rms">—</b></p>
              <p>Error p95 <b id="quality-p95">—</b></p>
              <p>Error máximo <b id="quality-max">—</b></p>
              <small>Los errores corresponden solo a puntos asignados; «—» significa que no hay muestras.</small>
              <label>Tolerancia (mm)<input id="projection-tolerance" type="number" min="0.1" max="200" step="0.1" value="5" /></label>
              <button id="apply-p0" class="popover-button" type="button">CARGAR P0 · 16 LÁSERES / 6 PLANOS</button>
              <button id="quick-export" class="popover-button" type="button">EXPORTAR CONFIGURACIÓN</button>
              <button id="quick-import" class="popover-button" type="button">IMPORTAR CONFIGURACIÓN</button>
              <button id="export-measurements" class="popover-button" type="button">PLANTILLA DE MEDICIONES JSON</button>
              <button id="import-measurements" class="popover-button" type="button">COMPARAR MEDICIONES</button>
              <input id="measurements-file" type="file" accept="application/json,.json" hidden />
              <small id="measurement-summary">Sin mediciones físicas importadas.</small>
              <p id="public-status" role="status">Preparando simulación…</p>
            </div>
          </details>
          <small class="projection-note">Secuencia láser · <b id="projection-frame">FOTOGRAMA 01 / 72 · X 0° / Y 0° / Z 0°</b></small>
        </div>
        <div id="future-notice" class="future-notice" hidden>
          <span>PLANNED PHASE</span>
          <b id="future-title">Optical voxelization is not calculated yet.</b>
          <p>No synthetic result is displayed in place of missing physics.</p>
        </div>
        <div class="axis-widget" aria-hidden="true"><i class="axis-x">X</i><i class="axis-y">Y</i><i class="axis-z">Z</i></div>
        <div class="interaction-hint"><span>◉</span> orbit <span>⌁</span> pan <span>＋</span> zoom</div>
        <div class="ray-legend">
          <span><i class="legend-success"></i>reaches target</span>
          <span><i class="legend-blocked"></i>blocked first</span>
          <span><i class="legend-desired"></i>desired path</span>
        </div>
      </section>

      <aside class="statistics-panel" aria-live="polite">
        <header><div><span>LIVE ANALYSIS</span><h3>MEASUREMENT</h3></div><i id="run-indicator"></i></header>
        <div class="metric major"><span>Target accessible</span><strong id="stat-success">—</strong><small id="stat-success-percent">—</small></div>
        <div class="metric-pair">
          <div class="metric"><span>Meshes</span><strong id="stat-meshes">—</strong></div>
          <div class="metric"><span>Filaments</span><strong id="stat-filaments">—</strong></div>
        </div>
        <div class="metric-pair">
          <div class="metric"><span id="stat-rays-label">Active rays</span><strong id="stat-rays">—</strong></div>
          <div class="metric warning"><span>Blocked</span><strong id="stat-blocked">—</strong></div>
        </div>
        <div class="bar-metric">
          <div><span>Occlusion rate</span><b id="stat-occlusion">—</b></div>
          <div class="metric-bar"><i id="occlusion-bar"></i></div>
        </div>
        <div class="metric-list">
          <p><span>Missed intersections</span><b id="stat-missed">—</b></p>
          <p><span>Rejected mesh poses</span><b id="stat-rejected">—</b></p>
          <p><span>Measured near-clearance</span><b id="stat-clearance">—</b></p>
          <p><span>Simulation time</span><b id="stat-time">—</b></p>
          <p><span>Frame rate</span><b id="stat-fps">—</b></p>
        </div>
        <div class="phase-metrics">
          <span>PROJECTION OUTPUT</span>
          <p>Loaded figure <b id="stat-figure">HEART</b></p>
          <p>Illuminated hits <b id="stat-lightpoints">—</b></p>
          <p>Fixed voxel pairs <b id="stat-voxels">—</b></p>
          <p>Requested / active <b id="stat-requested-active">—</b></p>
          <p>Matched-only error RMS <b id="stat-error-rms">—</b></p>
          <p>Matched-only error p95 <b id="stat-error-p95">—</b></p>
          <p>Matched-only error max <b id="stat-error-max">—</b></p>
          <p>Mean active spacing <b id="stat-mean-separation">—</b></p>
          <p>Point source <b>physical first hits</b></p>
        </div>
        <footer><span id="simulation-status">READY</span><small>Seed <b id="seed-value">—</b></small></footer>
      </aside>
    </main>
  </div>
`;
